/* eslint-disable no-template-curly-in-string */
/* eslint-disable max-len */
/* eslint-disable no-console */
import React from "react";
import { connect } from "react-redux";
import { Formik } from "formik";
import Grid from "@mui/material/Grid";
import * as Yup from "yup";
// import uniqueId from "lodash/uniqueId";
import { format, isValid } from "date-fns";
import get from "lodash/get";

import { getSignatories } from "../../../../../store/selectors/appConfig";
import {
  SelectAutocomplete,
  AutoSaveField as Field,
  DatePicker,
  Input,
  InlineRadioGroup
} from "../../../../../components/Form";
import { DATE_FORMAT } from "../../../../../constants";

import { SubmitButton } from "../../../components/SubmitButton/SubmitButton";
import {
  getInvalidMessage,
  getMinDateMessage,
  getNotTrimmedMessage,
  getRequiredMessage
} from "../../../../../utils/getValidationMessage";
import { YesNoList } from "../../../../../constants/options";
import { SPECIAL_CHARACTERS_REGEX, checkIsTrimmed } from "../../../../../utils/validation";
import { documentUnique } from "../../../../../constants/documents";
import { RoDocument } from "../../../../UploadDocuments/components/RoDocument/RoDocument";
import { InfoNoteStyled, FormStyle } from "../styled";
import { ScrollToError } from "components/ScrollToError";

const currentYear = format(new Date(), "yyyy");
export const signatoryRightsSchema = Yup.object().shape({
  involvementSince: Yup.date()
    .nullable()
    .min(new Date(1900, 0, 1), getMinDateMessage("Involvement since"))
    .max(new Date(), getInvalidMessage("Involvement since"))
    .typeError(getInvalidMessage("Involvement since")),
  educationBackground: Yup.string()
    .nullable()
    .max(10000, "Maximum 10000 characters allowed"),
  nameOfOrganisation: Yup.string()
    .nullable()
    .max(30, "Maximum ${max} characters allowed")
    .matches(SPECIAL_CHARACTERS_REGEX, getInvalidMessage("Name of organization"))
    .test("space validation", getNotTrimmedMessage("Name of organization"), checkIsTrimmed),
  designation: Yup.string().nullable(),
  numberOfYearsWorked: Yup.string().nullable(),
  backgroundOfSignatory: Yup.string()
    .required(getRequiredMessage("Background information of the Signatory/ Shareholder"))
    .max(1000, "Maximum ${max} characters allowed"),
  numberOfYearsInUAE: Yup.date()
    .nullable()
    .min(new Date(1900, 0, 1), getMinDateMessage("Signatory in UAE since which year?"))
    .max(new Date(currentYear, 11, 31), getInvalidMessage("Signatory in UAE since which year?"))
    .typeError(getInvalidMessage("Signatory in UAE since which year?")),
  howSuccessionRiskMitigated: Yup.string().nullable(),
  remarks: Yup.string().nullable(),
  borrowingPowers: Yup.string().nullable(),
  borrowingPowersDocument: Yup.string(),
  borrowingPowerSince: Yup.date()
    .nullable()
    .min(new Date(1900, 0, 1), getMinDateMessage("Borrowing powers since"))
    .max(new Date(), getInvalidMessage("Borrowing powers since"))
    .typeError(getInvalidMessage("Borrowing powers since"))
});

export const initialValues = {
  involvementSince: "",
  educationBackground: "",
  nameOfOrganisation: "",
  designation: "",
  numberOfYearsWorked: "",
  backgroundOfSignatory: "",
  numberOfYearsInUAE: "",
  howSuccessionRiskMitigated: "",
  others: "",
  borrowingPowers: "",
  borrowingPowersDocument: "",
  borrowingPowerSince: ""
};

export const changeDateProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(value, DATE_FORMAT) };

export const SignatoryRightsComponent = ({
  handleContinue,
  index,
  createFormChangeHandler,
  isSignatory,
  stakeholder: { fullName, signatoryId }
}) => {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleContinue}
      validationSchema={signatoryRightsSchema}
      validateOnChange={false}
    >
      {createFormChangeHandler(({ values }) => {
        const signatoryPath = `roProspect.signatoryInfo[${index}]`;
        return (
          <FormStyle>
            <ScrollToError />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                {!isSignatory && (
                  <InfoNoteStyled text={`${fullName || "This person"} is not a signatory`} />
                )}
                <h5 className="mb-0"> Signatory </h5>
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="involvementSince"
                  placeholder="Involvement since"
                  label="Involvement since"
                  path={`${signatoryPath}.involvementSince`}
                  component={DatePicker}
                  disabled={!isSignatory}
                  changeProspect={changeDateProspectHandler}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  maxDate={new Date()}
                />
              </Grid>
              {signatoryId && isSignatory && (
                <Grid item sm={12} xs={12}>
                  <RoDocument
                    uniqueId={documentUnique.SignatoryInvolvementInBusiness}
                    stakeholderIndex={`${index}_${signatoryId}`}
                  />
                </Grid>
              )}
              <Grid item sm={12} xs={12}>
                <Field
                  name="educationBackground"
                  label="Education background"
                  placeholder="Education background"
                  path={`${signatoryPath}.signatoryWorkExperience.educationBackground`}
                  multiline
                  rows="4"
                  disabled={!isSignatory}
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <h5 className="mb-0"> Latest Work Experience </h5>
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="nameOfOrganisation"
                  label="Name of Organization"
                  path={`${signatoryPath}.signatoryWorkExperience.companyName`}
                  placeholder="Name of Organization"
                  disabled={!isSignatory}
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="designation"
                  label="Designation"
                  placeholder="Designation"
                  path={`${signatoryPath}.signatoryWorkExperience.designation`}
                  component={SelectAutocomplete}
                  datalistId="individualOccupation"
                  disabled={!isSignatory}
                  tabIndex="0"
                  isSearchable
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="numberOfYearsWorked"
                  label="Number of years worked"
                  path={`${signatoryPath}.signatoryWorkExperience.noOfYearsWorked`}
                  component={SelectAutocomplete}
                  datalistId="numYearsWorkedSA"
                  disabled={!isSignatory}
                  tabIndex="0"
                  isSearchable
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="backgroundOfSignatory"
                  label={"Background information of the Signatory/ Shareholder"}
                  placeholder={"Background information of the Signatory/ Shareholder"}
                  path={`${signatoryPath}.signatoryWorkExperience.signatoryBackgroundInfo`}
                  multiline
                  rows="4"
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0, maxLength: 1000 }
                  }}
                />
              </Grid>
              <Grid item sm={7} xs={12}>
                <Field
                  name="numberOfYearsInUAE"
                  label="Signatory in UAE since which year?"
                  path={`${signatoryPath}.signatoryWorkExperience.inUAESince`}
                  component={DatePicker}
                  openTo="year"
                  views={["year"]}
                  disabled={!isSignatory}
                  format="yyyy"
                  placeholder="____"
                  disableFuture
                  changeProspect={changeDateProspectHandler}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  maxDate={new Date()}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="howSuccessionRiskMitigated"
                  label="How is the succession risk mitigated?"
                  path={`${signatoryPath}.signatoryWorkExperience.howSuccessionRiskMitigated.riskMitigated`}
                  component={SelectAutocomplete}
                  datalistId="successionRiskSA"
                  disabled={!isSignatory}
                  tabIndex="0"
                  isSearchable
                />
              </Grid>
              {values.howSuccessionRiskMitigated === "R5" && (
                <Grid item sm={12} xs={12}>
                  <Field
                    name="remarks"
                    placeholder="Remarks"
                    path={`${signatoryPath}.signatoryWorkExperience.howSuccessionRiskMitigated.others`}
                    multiline
                    disabled={!isSignatory}
                    rows="4"
                    component={Input}
                    InputProps={{
                      inputProps: { tabIndex: 0 }
                    }}
                  />
                </Grid>
              )}

              <Grid item sm={12} xs={12}>
                <h5> Borrowing Powers </h5>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                <Field
                  name="borrowingPowers"
                  label="Borrowing powers"
                  path={`${signatoryPath}.borrowingPower`}
                  component={InlineRadioGroup}
                  options={YesNoList}
                  disabled={!isSignatory}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="borrowingPowerDoc"
                  label="Borrowing powers documents"
                  path={`${signatoryPath}.borrowingPowerDoc`}
                  component={SelectAutocomplete}
                  datalistId="borrPowersDocSA"
                  tabIndex="0"
                  isSearchable
                  disabled={!isSignatory}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="borrowingPowerSince"
                  path={`${signatoryPath}.borrowingPowerSince`}
                  label="Borrowing powers since"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  maxDate={new Date()}
                  disabled={!isSignatory}
                />
              </Grid>
            </Grid>
            <SubmitButton />
          </FormStyle>
        );
      })}
    </Formik>
  );
};

const mapStateToProps = (state, { index }) => ({
  isSignatory: get(getSignatories(state)[index], "isSignatory", false)
});

export const SignatoryRights = connect(mapStateToProps)(SignatoryRightsComponent);
